import React, { useEffect, useState } from 'react';
import RefreshCheckApi from '../../communication_system/axios_apis/refresh_check/refresh_check_api';

const endpoint = '/adminSyrup/qa-recommendation';
const receiptKey = (a, b) => `sirup-recommendation-qa:${a}:${b}`;
const readReceipt = (a, b) => {
    try { return localStorage.getItem(receiptKey(a, b)) || ''; } catch { return ''; }
};

export default function SirupRecommendationQA() {
    const [viewer, setViewer] = useState('');
    const [target, setTarget] = useState('');
    const [enabled, setEnabled] = useState(false);
    const [environment, setEnvironment] = useState('');
    const [checking, setChecking] = useState(true);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [ready, setReady] = useState(false);
    const [receipt, setReceipt] = useState('');

    useEffect(() => {
        let active = true;
        RefreshCheckApi.get(endpoint).then(({ data }) => {
            if (active) {
                const validEnvironment = data?.environment === 'development' || data?.environment === 'production';
                setEnabled(data?.ok === true && validEnvironment);
                setEnvironment(validEnvironment ? data.environment : '');
                if (!validEnvironment) setMessage('서버 환경을 확인하지 못했습니다. 페이지를 새로고침해 주세요.');
            }
        }).catch((error) => {
            if (active) setMessage(error.response?.data?.message || 'QA 서버 환경을 확인하지 못했습니다. 다시 로그인하거나 페이지를 새로고침해 주세요.');
        }).finally(() => { if (active) setChecking(false); });
        return () => { active = false; };
    }, []);

    useEffect(() => {
        setReceipt(readReceipt(viewer, target));
        setReady(false);
        setMessage('');
    }, [viewer, target]);

    const valid = /^[1-9][0-9]*$/.test(viewer) && /^[1-9][0-9]*$/.test(target)
        && Number(viewer) <= 2147483647 && Number(target) <= 2147483647 && viewer !== target;

    async function submit(action) {
        if (!valid || busy || !enabled) return;
        setBusy(true);
        setReady(false);
        setMessage('');
        try {
            const { data } = await RefreshCheckApi.post(endpoint, {
                viewer_id: Number(viewer), target_id: Number(target), action,
                ...(receipt ? { receipt } : {}),
            });
            if (!data?.ok) throw new Error('unexpected response');
            const result = data.data;
            setReady(result.ready === true);
            setMessage(result.message);
            const nextReceipt = result.receipt || '';
            setReceipt(nextReceipt);
            try {
                if (nextReceipt) localStorage.setItem(receiptKey(viewer, target), nextReceipt);
                else localStorage.removeItem(receiptKey(viewer, target));
            } catch {
                setMessage(`${result.message} 이 브라우저에 정리 기록을 보관하지 못했습니다. 카드 준비 결과는 유지됩니다.`);
            }
        } catch (error) {
            setMessage(error.response?.data?.message || '준비 결과를 확인하지 못했습니다. 다시 확인해 주세요.');
        } finally { setBusy(false); }
    }

    return (
        <main style={{ maxWidth: 720, padding: 24 }}>
            <h1>시럽 쌍방 테스트</h1>
            {environment && <p><strong>현재 환경: {environment === 'production' ? '운영' : '개발'}</strong> · 이 환경에 가입한 테스트 계정 ID를 입력해 주세요.</p>}
            <p>현재 어드민과 같은 환경의 테스트 계정 두 개를 준비해 주세요. A에게 B의 추천 카드를 보여줍니다.</p>
            <p>계정 ID는 기존 ‘시럽 데이팅앱 관리’에서 테스트 회원을 검색해 확인할 수 있습니다.</p>
            <p>처음부터 다시 시험할 때는 새 B 계정을 사용하세요. 기존 편지나 대화는 지우지 않습니다.</p>
            {checking && <p role="status">서버 환경을 확인하고 있습니다.</p>}
            <form onSubmit={(event) => { event.preventDefault(); submit('setup'); }}>
                <label style={{ display: 'block', marginTop: 20 }}>
                    A 계정 ID · 추천을 보는 사람
                    <input aria-label="A 계정 ID" inputMode="numeric" value={viewer} disabled={busy || !enabled}
                        onChange={(event) => setViewer(event.target.value.trim())}
                        style={{ display: 'block', width: '100%', padding: 12, marginTop: 8 }} />
                </label>
                <label style={{ display: 'block', marginTop: 20 }}>
                    B 계정 ID · 추천에 보여줄 사람
                    <input aria-label="B 계정 ID" inputMode="numeric" value={target} disabled={busy || !enabled}
                        onChange={(event) => setTarget(event.target.value.trim())}
                        style={{ display: 'block', width: '100%', padding: 12, marginTop: 8 }} />
                </label>
                {viewer && target && !valid && <p role="alert">서로 다른 테스트 계정 ID를 입력해 주세요.</p>}
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    <button type="submit" disabled={!enabled || !valid || busy} style={{ padding: '12px 24px' }}>
                        {busy ? '확인 중…' : '추천 준비'}
                    </button>
                    <button type="button" disabled={!enabled || !valid || !receipt || busy}
                        onClick={() => submit('reset')} style={{ padding: '12px 16px' }}>준비한 카드 정리</button>
                </div>
            </form>
            {message && <p role={ready ? 'status' : 'alert'} style={{ marginTop: 24, whiteSpace: 'pre-line' }}>{message}</p>}
            <h2 style={{ marginTop: 32 }}>앱에서 확인할 순서</h2>
            <ol>
                <li>A의 같은 환경 시럽 앱에서 추천 화면을 새로고침합니다.</li>
                <li>B의 카드를 선택하고 기존 화면에서 편지를 보냅니다.</li>
                <li>B의 같은 환경 앱에서 알림과 받은 편지를 확인하고 수락합니다.</li>
                <li>매칭된 대화방에 들어가 대화가 이어지는지 확인합니다.</li>
            </ol>
            <p>이 페이지는 추천 카드만 준비합니다. 하트 지급, 편지 발송, 알림 발송, 편지 수락은 자동으로 하지 않습니다.</p>
        </main>
    );
}
